import React from "react";
import { Button } from "@nextui-org/react";
import { MdErrorOutline, MdRefresh } from "react-icons/md";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // You can also log the error to an error reporting service
        console.error("Uncaught error:", error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return (
                <div className="h-screen w-full flex flex-col items-center justify-center bg-[#f0f8ff] p-4 text-center">
                    <div className="bg-white p-10 rounded-3xl shadow-2xl max-w-md w-full border border-red-50 relative overflow-hidden">
                        {/* Decorative background element */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-400 via-orange-400 to-red-400"></div>
                        
                        <div className="p-5 rounded-2xl bg-red-50 text-red-500 w-fit mx-auto mb-6 shadow-sm">
                            <MdErrorOutline size={56} />
                        </div>
                        
                        <h1 className="text-2xl font-extrabold text-gray-900 mb-3 tracking-tight">Oops! Something went sideways</h1>
                        
                        <div className="space-y-4 mb-8">
                            <p className="text-gray-600 leading-relaxed text-sm">
                                We've encountered an unexpected issue while loading this page. Usually, these glitches are temporary.
                            </p>
                            
                            <div className="text-xs text-left bg-slate-50 p-5 rounded-2xl border border-slate-100 transition-all hover:bg-slate-100/50">
                                <p className="font-bold mb-3 text-slate-800 uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                                    Possible Causes
                                </p>
                                <ul className="space-y-2 text-slate-600 font-medium">
                                    <li className="flex items-start gap-2">
                                        <span className="text-red-400">•</span>
                                        Slow or interrupted internet connection
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-red-400">•</span>
                                        A brief system hiccup or server delay
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-red-400">•</span>
                                        The security session might have expired
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <Button
                            color="primary"
                            size="lg"
                            className="w-full font-bold shadow-lg shadow-blue-100 py-6 rounded-xl"
                            startContent={<MdRefresh size={24} />}
                            onPress={this.handleReset}
                        >
                            Refresh Application
                        </Button>
                        
                        <p className="mt-6 text-gray-400 text-xs font-medium">
                            If the problem persists, please contact support.
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
